import {
  Box,
  Card,
  type Theme,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import {
  CalendarIcon,
  CheckCircleIcon,
  DocumentIcon,
  RefreshIcon,
  ReminderIcon,
} from '../../../icons';
import { Button } from '../../atoms/Button';
import { Tag } from '../../atoms/Tag';
import { HTMLDisplay } from '../HTMLDisplay';
import { ProviderLink } from '../ProviderLink';
import { TopicLink } from '../TopicLink';
import { ResourceMenuActions } from '../../organisms/ResourceMenuActions';
import { REMINDER_CARD_LINK_ATTRIBUTE, ReminderCardTitleLink } from './ReminderCardTitleLink';
import {
  type ReminderCardAction,
  type ReminderCardProps,
  type ReminderItem,
  type ReminderRecurrenceType,
  useReminderCard,
} from './useReminderCard';

type Props = ReminderCardProps;

function clickableCardSx(isClickable: boolean) {
  if (!isClickable) {
    return { position: 'relative' } as const;
  }

  return (theme: Theme) => ({
    position: 'relative',
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    [`&:has([${REMINDER_CARD_LINK_ATTRIBUTE}]:focus-visible)`]: {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    // Links and menus inside the card sit above the stretched title link.
    [`& a:not([${REMINDER_CARD_LINK_ATTRIBUTE}]), & button:not([${REMINDER_CARD_LINK_ATTRIBUTE}])`]: {
      position: 'relative',
      zIndex: 1,
    },
  });
}

export function ReminderCard(props: Props) {
  const hook = useReminderCard(props);
  const titleContent = hook.isClickable ? (
    <ReminderCardTitleLink href={props.href} onClick={props.onClick}>
      {hook.title}
    </ReminderCardTitleLink>
  ) : (
    hook.title
  );

  if (props.variant === 'condensed') {
    return (
      <Card
        variant="outlined"
        data-testid={props['data-testid']}
        sx={[clickableCardSx(hook.isClickable), { p: { xs: 1.25, sm: 1.5 } }]}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: 'center' }}
        >
          {/* Left Column: Date / Icon Block */}
          {hook.hasDate ? (
            <Stack
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 46,
                flexShrink: 0,
                borderRadius: 1,
                bgcolor: 'background.elevation1',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body1Emphasis">
                {hook.dayNumber}
              </Typography>
              <Typography
                variant="body2Emphasis"
                sx={{
                  color: hook.accentColor,
                  textTransform: 'uppercase',
                }}
              >
                {hook.monthAbbr}
              </Typography>
            </Stack>
          ) : (
            <Stack
              sx={(theme) => ({
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 46,
                flexShrink: 0,
                borderRadius: 1,
                bgcolor: theme.palette.resourcesColors?.rappel?.light ?? '#fff3e0',
                color: theme.palette.resourcesColors?.rappel?.main ?? '#b54500',
              })}
            >
              <ReminderIcon fontSize="small" />
            </Stack>
          )}

          {/* Center Column: Title & Metadata */}
          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Typography
                variant="body1Emphasis"
                component="span"
                sx={{
                  textDecoration: hook.isCompleted ? 'line-through' : 'none',
                  color: hook.isCompleted ? 'text.secondary' : 'text.primary',
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                }}
              >
                {titleContent}
              </Typography>

              {hook.isCompleted ? (
                <Tag
                  color="done"
                  label="Terminé"
                  variantIcon="state"
                />
              ) : hook.isOverdue ? (
                <Tag
                  color="incident"
                  label="Dépassé"
                  variantIcon="state"
                />
              ) : null}

              {hook.recurrenceLabel ? (
                <Tag
                  color="default"
                  label={hook.recurrenceLabel}
                  icon={<RefreshIcon fontSize="small" />}
                />
              ) : null}
            </Stack>

            {/* Secondary line: Subject, Provider & Time in caption */}
            {(hook.showSubject && (props.renderSubject || hook.subjectName)) ||
            (hook.showProvider && (props.renderProvider || hook.providerName)) ||
            hook.hasDate ? (
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                {hook.showSubject && (props.renderSubject || hook.subjectName) ? (
                  props.renderSubject ? (
                    props.renderSubject()
                  ) : props.subjectHref || props.onSubjectClick ? (
                    <TopicLink
                      name={hook.subjectName}
                      href={props.subjectHref}
                      onClick={props.onSubjectClick}
                      variant="caption"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {hook.subjectName}
                    </Typography>
                  )
                ) : null}

                {hook.showSubject &&
                (props.renderSubject || hook.subjectName) &&
                hook.showProvider &&
                (props.renderProvider || hook.providerName) ? (
                  <Typography variant="caption" color="text.secondary">
                    ·
                  </Typography>
                ) : null}

                {hook.showProvider && (props.renderProvider || hook.providerName) ? (
                  props.renderProvider ? (
                    props.renderProvider()
                  ) : props.providerHref || props.onProviderClick ? (
                    <ProviderLink
                      name={hook.providerName}
                      href={props.providerHref}
                      onClick={props.onProviderClick}
                      variant="caption"
                    />
                  ) : (
                    <ProviderLink
                      name={hook.providerName}
                      variant="caption"
                    />
                  )
                ) : null}

                {((hook.showSubject && (props.renderSubject || hook.subjectName)) ||
                  (hook.showProvider && (props.renderProvider || hook.providerName))) &&
                hook.hasDate ? (
                  <Typography variant="caption" color="text.secondary">
                    ·
                  </Typography>
                ) : null}

                {hook.hasDate && (hook.timeText || (!hook.showSubject && !hook.showProvider)) ? (
                  <Typography variant="caption" color="text.secondary">
                    {hook.timeText ? hook.timeText : hook.fullDate}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
          </Stack>

          {/* Right Column: every action in one menu, nothing tappable by mistake */}
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, alignItems: 'center' }}>
            {props.dragHandle}
            {props.action ? (
              props.action
            ) : (
              <ResourceMenuActions
                size="small"
                actions={hook.condensedActions}
                tooltip={hook.actionsButtonTooltip}
                ariaLabel={hook.actionsButtonAriaLabel}
              />
            )}
          </Stack>
        </Stack>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      data-testid={props['data-testid']}
      sx={[clickableCardSx(hook.isClickable), { p: { xs: 2, sm: 2.5 } }]}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 2.5 }}
        sx={{ alignItems: { sm: 'stretch' } }}
      >
        {/* Left Column: Date & Status Block */}
        <Stack
          spacing={0.5}
          sx={{
            justifyContent: 'center',
            width: { sm: 190 },
            minWidth: { sm: 190 },
            maxWidth: { sm: 190 },
            flexShrink: 0,
          }}
        >
          {hook.hasDate ? (
            <>
              <Typography
                variant="body2Emphasis"
                sx={{ color: hook.accentColor, lineHeight: 1.3 }}
              >
                {hook.dayName}
              </Typography>
              <Typography
                variant="body1Emphasis"
                sx={{ lineHeight: 1.25 }}
              >
                {hook.fullDate}
              </Typography>
              {hook.timeText ? (
                <Typography variant="body2" color="text.secondary">
                  {hook.timeText}
                </Typography>
              ) : null}
            </>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.resourcesColors?.rappel?.light ?? '#fff3e0',
                    color: theme.palette.resourcesColors?.rappel?.main ?? '#b54500',
                  })}
                >
                  <ReminderIcon fontSize="small" />
                </Box>
                <Typography
                  variant="body2Emphasis"
                  sx={{ color: hook.accentColor, lineHeight: 1.3 }}
                >
                  Rappel
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Sans échéance
              </Typography>
            </>
          )}

          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            {hook.isCompleted ? (
              <Tag
                color="done"
                label="Terminé"
                variantIcon="state"
              />
            ) : hook.isOverdue ? (
              <Tag
                color="incident"
                label="Date dépassée"
                variantIcon="state"
              />
            ) : (
              <Tag
                color="rappel"
                label="En cours"
                variantIcon="state"
              />
            )}

            {hook.recurrenceLabel ? (
              <Tag
                color="default"
                label={hook.recurrenceLabel}
                icon={<RefreshIcon fontSize="small" />}
              />
            ) : null}
          </Stack>
        </Stack>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: 'none', sm: 'block' } }}
        />
        <Divider
          orientation="horizontal"
          sx={{ display: { xs: 'block', sm: 'none' } }}
        />

        {/* Right Column: Main Content */}
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Typography variant="body1Emphasis" component="span">
                {titleContent}
              </Typography>

              {hook.showProvider && (props.renderProvider || hook.providerName) ? (
                props.renderProvider ? (
                  props.renderProvider()
                ) : props.providerHref || props.onProviderClick ? (
                  <ProviderLink
                    name={hook.providerName}
                    href={props.providerHref}
                    onClick={props.onProviderClick}
                    variant="body2Emphasis"
                  />
                ) : (
                  <ProviderLink
                    name={hook.providerName}
                    variant="body2Emphasis"
                  />
                )
              ) : null}
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, alignItems: 'center' }}>
              {props.dragHandle}
              {props.action ? (
                props.action
              ) : hook.hasActions ? (
                <ResourceMenuActions
                  size="small"
                  actions={hook.actions}
                  tooltip={hook.actionsButtonTooltip}
                  ariaLabel={hook.actionsButtonAriaLabel}
                />
              ) : null}
            </Stack>
          </Stack>

          {/* Subject metadata */}
          {hook.showSubject && (hook.subjectName || props.renderSubject) ? (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Sujet :
              </Typography>
              {props.renderSubject ? (
                props.renderSubject()
              ) : props.subjectHref || props.onSubjectClick ? (
                <TopicLink
                  name={hook.subjectName}
                  href={props.subjectHref}
                  onClick={props.onSubjectClick}
                  variant="caption"
                />
              ) : (
                <TopicLink
                  name={hook.subjectName}
                  variant="caption"
                />
              )}
            </Stack>
          ) : null}

          {/* Description */}
          {hook.hasDescription ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Stack sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}>
                <DocumentIcon fontSize="small" />
              </Stack>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: 1.45,
                }}
              >
                {props.renderDescription ? (
                  props.renderDescription(hook.description!)
                ) : (
                  <HTMLDisplay
                    content={hook.description!}
                    maxLines={props.maxDescriptionLines ?? 3}
                  />
                )}
              </Box>
            </Stack>
          ) : null}

          {/* Action buttons (Footer) */}
          {hook.hasFooterActions ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                pt: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              {props.footerActions ? (
                props.footerActions
              ) : hook.isCompleted ? (
                props.onReopen ? (
                  <Button
                    size="small"
                    variant="text"
                    color="default"
                    startIcon={<RefreshIcon fontSize="small" />}
                    onClick={hook.handleReopen}
                  >
                    Repasser en cours
                  </Button>
                ) : null
              ) : (
                <>
                  {props.onCreateEvent ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<CalendarIcon fontSize="small" />}
                      onClick={hook.handleCreateEvent}
                    >
                      Créer un rendez-vous et terminer
                    </Button>
                  ) : null}

                  {props.onComplete ? (
                    <Button
                      size="small"
                      variant={props.onCreateEvent ? "outlined" : "contained"}
                      color="primary"
                      startIcon={<CheckCircleIcon fontSize="small" />}
                      onClick={hook.handleComplete}
                      loading={props.isCompleting}
                    >
                      Marquer comme terminé
                    </Button>
                  ) : null}
                </>
              )}
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
}

export const ReminderListItem = (props: ReminderCardProps) => (
  <ReminderCard {...props} variant="condensed" />
);

export type {
  ReminderCardAction,
  ReminderCardProps,
  ReminderItem,
  ReminderRecurrenceType,
};

